import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import type { Game, GameKind } from '@talkvibe/shared';
import { rulesFor } from '@talkvibe/shared';

import { ApiError } from '../services/api.ts';
import { gameService } from '../services/games.ts';
import { useAuthStore } from './auth.ts';

/**
 * The game being played in the conversation that is open.
 *
 * **One game, because one is all there can be.** The server allows a single
 * active game per conversation — a UNIQUE key, not a convention — so this holds
 * one rather than a map keyed by conversation. Opening a different chat loads
 * that chat's game over the top.
 *
 * **The server is the only authority.** Every function here sends the intent
 * and takes back whatever game comes out; nothing computes a new board locally
 * and keeps it. The rules are imported to decide what to *draw* — which squares
 * are tappable, which line to highlight — so the interface offers exactly the
 * moves the API would accept, and never a move that bounces.
 */
export const useGameStore = defineStore('game', () => {
  const game = ref<Game | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  /**
   * The square being played, so it can be shown as pending.
   *
   * A move is a round trip, and on a slow connection a board that does nothing
   * for half a second reads as a square that did not register — which is how
   * somebody taps a second one.
   */
  const pendingCell = ref<number | null>(null);
  /** True while the chooser is open, before any game exists to show. */
  const choosing = ref(false);

  const me = computed(() => useAuthStore().user?.id ?? null);

  /** The mark the signed-in player is using, or null if they are not playing. */
  const myMark = computed<'X' | 'O' | null>(() => {
    const id = me.value;
    if (!game.value || !id) return null;
    return game.value.players.find((player) => player.user.id === id)?.mark ?? null;
  });

  const isMyTurn = computed(
    () => game.value?.status === 'active' && game.value.turnUserId === me.value,
  );

  const opponent = computed(
    () => game.value?.players.find((player) => player.user.id !== me.value)?.user ?? null,
  );

  /**
   * Where a tap on this square actually plays, or null if it plays nothing.
   *
   * Asked of the rules rather than assumed, because Connect Four has gravity: a
   * tap anywhere in a column means the piece at the bottom of it. A board that
   * made people aim at the lowest empty square would be a worse game for no
   * reason, and one that sent the tapped square would have every move refused.
   */
  function targetOf(cell: number): number | null {
    const current = game.value;
    if (!current) return null;
    return rulesFor(current.kind).resolveTap(current.board, cell);
  }

  /** Whether a square can be tapped right now — the same rules the server applies. */
  function canPlay(cell: number): boolean {
    const mark = myMark.value;
    const current = game.value;
    if (!current || !mark) return false;
    if (pendingCell.value !== null) return false;
    const target = targetOf(cell);
    if (target === null) return false;
    return rulesFor(current.kind).isLegalMove(current.board, target, mark);
  }

  /**
   * Loads whatever game that conversation has, if any.
   *
   * Clears first, so a slow response cannot leave the previous chat's board on
   * screen under the new chat's name.
   */
  async function load(conversationId: string): Promise<void> {
    game.value = null;
    error.value = null;
    pendingCell.value = null;
    loading.value = true;
    try {
      game.value = await gameService.active(conversationId);
    } catch {
      // A chat with no game is the normal case and already answers null, so
      // anything thrown here is a network blip. Showing an error for it would
      // put a failure on a screen nobody asked to play on.
      game.value = null;
    } finally {
      loading.value = false;
    }
  }

  async function start(conversationId: string, kind: GameKind = 'tictactoe'): Promise<void> {
    error.value = null;
    loading.value = true;
    choosing.value = false;
    try {
      game.value = await gameService.start(conversationId, kind);
    } catch (caught) {
      error.value = messageFor(caught);
    } finally {
      loading.value = false;
    }
  }

  async function play(cell: number): Promise<void> {
    const current = game.value;
    if (!current || !canPlay(cell)) return;
    // The square the piece lands on, which is the tapped one in every game
    // without gravity and the bottom of the column in the one with it.
    const target = targetOf(cell);
    if (target === null) return;
    error.value = null;
    pendingCell.value = target;
    try {
      game.value = await gameService.move(current.id, target);
    } catch (caught) {
      error.value = messageFor(caught);
      // Re-read rather than trusting the local board: the usual reason a move
      // is refused is that the other player got there first, which means what
      // is on screen is already out of date.
      try {
        game.value = await gameService.byId(current.id);
      } catch {
        // Leave the board as it was; the error line says what happened.
      }
    } finally {
      pendingCell.value = null;
    }
  }

  async function resign(): Promise<void> {
    const current = game.value;
    if (!current || current.status !== 'active') return;
    try {
      game.value = await gameService.resign(current.id);
    } catch (caught) {
      error.value = messageFor(caught);
    }
  }

  /**
   * A game arriving on the conversation socket.
   *
   * The whole game travels, so this is a replacement rather than a merge — and
   * that is what makes a missed frame harmless: the next one is complete.
   *
   * Ignored while a move of our own is in flight, because the two would race:
   * the frame for the opponent's move can arrive before our own reply, and
   * applying it would show a board our pending move is about to replace.
   */
  function receive(incoming: Game): void {
    if (pendingCell.value !== null) return;
    if (game.value && game.value.id !== incoming.id && game.value.status === 'active') {
      // A frame for a different game in the same conversation. The server only
      // allows one active at a time, so this is a rematch starting - take it.
      game.value = incoming;
      return;
    }
    game.value = incoming;
  }

  /** Clears the board when the conversation is left. */
  function reset(): void {
    game.value = null;
    error.value = null;
    pendingCell.value = null;
    loading.value = false;
    choosing.value = false;
  }

  return {
    game,
    loading,
    error,
    pendingCell,
    choosing,
    targetOf,
    myMark,
    isMyTurn,
    opponent,
    canPlay,
    load,
    start,
    play,
    resign,
    receive,
    reset,
  };
});

/** The server's own words where there are any, since they say which rule was broken. */
function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.fieldError('cell') ?? caught.fieldError('conversationId') ?? caught.message;
  }
  return 'Something went wrong.';
}
